import { useParams } from 'react-router-dom';
import { useState } from 'react';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { DatePicker, Dropdown, ErrorContainer, Stack } from 'components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getMedicalVisits } from 'store/medicalVisits/medicalVisitsActions';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import style from './medicalVisits.module.scss';

dayjs.extend(isSameOrBefore);

const errorMessage = 'End Date should be on or after Start Date';

const MedicalVisitsFilters = () => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<{ endDate: string } | null>(null);
  const { medicalVisitsList, medicalVisitsTypes, medicalVisitsStatuses } = useAppSelector(
    (state) => state.medicalVisits
  );
  const { page, size, sorts } = medicalVisitsList;

  const formik = useFormik({
    initialValues: { startDate: '', endDate: '', typeId: [], statusId: [] },
    onSubmit: (values) => {
      if (clientId) {
        dispatch(
          getMedicalVisits({
            clientId,
            page,
            size,
            sorts,
            filters: {
              typeId: values.typeId?.length > 0 ? values.typeId : null,
              statusId: values.statusId?.length > 0 ? values.statusId : null,
              startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null,
              endDate: values.endDate ? dayjs(values.endDate).format('YYYY-MM-DD') : null
            }
          })
        );
      }
    }
  });

  const handleChange = (field: string, value: string | string[]) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true);

    formik.submitForm();
  };

  const handleStartDateChange = (value: string) => {
    formik.setFieldValue('startDate', value);
    formik.setFieldTouched('startDate', true);
    if (
      value &&
      formik.values.endDate &&
      value !== 'Invalid Date' &&
      !dayjs(value).isSameOrBefore(dayjs(formik.values.endDate))
    ) {
      setError({ endDate: errorMessage });
      return;
    }
    setError(null);
    formik.submitForm();
  };

  const handleEndDateChange = (value: string) => {
    formik.setFieldValue('endDate', value);
    formik.setFieldTouched('endDate', true);
    if (
      value &&
      formik.values.startDate &&
      value !== 'Invalid Date' &&
      !dayjs(formik.values.startDate).isSameOrBefore(dayjs(value))
    ) {
      setError({ endDate: errorMessage });
      return;
    }
    setError(null);
    formik.submitForm();
  };

  return (
    <Stack gap="8px" flexDirection="row" className={style.filterWrap}>
      <Dropdown
        className={clsx(style.filterSelect, formik.values.typeId.length > 0 && style.selected)}
        name="typeId"
        onChange={(e) => handleChange('typeId', e.target.value)}
        onBlur={formik.handleBlur}
        value={formik.values.typeId || []}
        data={medicalVisitsTypes}
        label="Type"
        multiSelect
        rounded
        fitWidth
        fitWidthMore={20}
      />
      <Dropdown
        className={clsx(style.filterSelect, formik.values.statusId.length > 0 && style.selected)}
        name="statusId"
        onChange={(e) => handleChange('statusId', e.target.value)}
        onBlur={formik.handleBlur}
        value={formik.values.statusId || []}
        data={medicalVisitsStatuses}
        label="Status"
        multiSelect
        rounded
      />
      <DatePicker
        classnamesProps={style.filterDatepicker}
        onChange={handleStartDateChange}
        value={formik.values.startDate}
        inputPlaceholder="Start Date"
        isFilter
      />
      <Stack width="100%" display="flex" flexDirection="column" gap="4px">
        <DatePicker
          classnamesProps={style.filterDatepicker}
          onChange={handleEndDateChange}
          value={formik.values.endDate}
          inputPlaceholder="End Date"
          isFilter
        />
        {error && <ErrorContainer autoHeight visible={!!error} name="endDate" errors={error} />}
      </Stack>
    </Stack>
  );
};

export default MedicalVisitsFilters;
